import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import hoistNonReactStatics from 'hoist-non-react-statics';
import { useAccount } from '~/hooks/with-account';
import { useOwnerQuery } from '~/graphql/eip721-subgraph/types';
import { Token } from '~/types/token';
import * as Sentry from '@sentry/react';
import { load, save } from '~/utils/local.storage.utils';
import { Erc721Metadata, fetchErc721Metadata } from '@gu-corp/react-ipfs-media';
import { NFTService } from '~/services/api/nft.service';

export enum FilterNFTsOption {
  ALL = 'all',
  FAVORITE = 'favorite',
  SPAM = 'spam',
}

export enum SortNFTsOption {
  LAST_SEEN = 'last_seen',
  NAME = 'A-Z',
}

export type OwnerToken = {
  id: string;
  tokenID: any;
  mintTime: any;
  tokenURI: string;
  contract: {
    id: string;
    name?: string | null;
    symbol?: string | null;
    doAllAddressesOwnTheirIdByDefault: boolean;
    supportsEIP721Metadata: boolean;
    numTokens: any;
    numOwners: any;
    tokens: Array<{
      id: string;
    }>;
  };
  owner: {
    id: string;
  };
};

export type Nft = OwnerToken & {
  tokenName?: string;
  isFavorite?: Record<string, boolean>;
  isSpam?: Record<string, boolean>;
  lastSeenAt?: Record<string, string>;
};

export type NftDataContextType = {
  nfts: Nft[];
  sortedAndFilteredNfts: Nft[];
  refetchNfts: () => void;
  updateFavourite: (token: Token) => void;
  updateSpam: (token: Token) => void;
  loading: boolean;
  fetchAndCacheNFT: (token: Nft, ipfsGateways: string[] | undefined) => Promise<Token | null>;
  setNftLastSeenAt: (tokenId: string, ownerAddress: string) => Token | null;
  onSearchNfts: (searchText: string) => void;
  onSelectFilterOption: (option: FilterNFTsOption) => void;
  onSelectSortNftsOption: (option: SortNFTsOption) => void;
  filterOption: FilterNFTsOption;
  sortOption: SortNFTsOption;
};

const NftDataContext = createContext<NftDataContextType>({
  nfts: [],
  sortedAndFilteredNfts: [],
  refetchNfts: () => {},
  updateFavourite: () => {},
  updateSpam: () => {},
  loading: false,
  fetchAndCacheNFT: () => Promise.resolve(null),
  setNftLastSeenAt: () => null,
  onSearchNfts: () => {},
  onSelectFilterOption: () => {},
  onSelectSortNftsOption: () => {},
  filterOption: FilterNFTsOption.ALL,
  sortOption: SortNFTsOption.LAST_SEEN,
});

export const withNftData = <P extends Record<string, unknown>>(
  ChildComponent: React.ComponentType<P>,
) => {
  const WithNftDataHOC = (props: P) => {
    const [nfts, setNfts] = useState<Nft[]>([]);
    const [searchText, setSearchText] = useState('');
    const [filterOption, setFilterOption] = useState(FilterNFTsOption.ALL);
    const [sortOption, setSortOption] = useState(SortNFTsOption.LAST_SEEN);
    const { account } = useAccount();
    const {
      data: ownerData,
      refetch,
      loading,
    } = useOwnerQuery({
      variables: {
        id: account.ethAddress,
      },
    });

    const getCachedNft = useCallback((tokenId: string): Token | undefined => {
      try {
        const cachedToken = load(tokenId);
        return cachedToken !== null ? JSON.parse(cachedToken) : undefined;
      } catch (error) {
        Sentry.captureException(error);
        return undefined;
      }
    }, []);

    const setCachedNft = useCallback(
      (token: any, metadata: Erc721Metadata | null): Token | null => {
        try {
          const cachedToken = {
            id: token.id,
            tokenId: token.tokenID.toString(),
            tokenUri: token.tokenURI,
            metadata: {
              image: metadata?.image || '/images/gu-logo.svg',
              name: metadata?.name || 'Unknown',
              animation_url: metadata?.animation_url || '',
              description: metadata?.description || '',
            },
            collection: {
              contractAddress: token.contract.id,
              name: token.contract.name,
              symbol: token.contract.symbol,
            },
            ownerAddress: token.owner.id,
            isFavorite: {
              [token.owner.id]: false,
            },
            isSpam: {
              [token.owner.id]: false,
            },
          };
          save(token.id, JSON.stringify(cachedToken));
          return cachedToken;
        } catch (error) {
          Sentry.captureException(error);
          return null;
        }
      },
      [],
    );

    const updateFavourite = useCallback(
      (token: Token) => {
        try {
          const cachedToken = getCachedNft(token.id);
          if (!cachedToken) {
            return;
          }
          const isFavorite =
            cachedToken.isFavorite !== undefined &&
            cachedToken.isFavorite[account.ethAddress] !== undefined
              ? !cachedToken.isFavorite[account.ethAddress]
              : true;
          cachedToken.isFavorite = {
            ...cachedToken.isFavorite,
            [account.ethAddress]: isFavorite,
          };
          save(token.id, JSON.stringify(cachedToken));

          const updatedNfts = nfts.map((nft) => {
            if (nft.id === token.id) {
              const updatedToken = { ...nft, isFavorite: cachedToken.isFavorite };
              return updatedToken;
            }
            return nft;
          });
          setNfts(updatedNfts);
        } catch (error) {
          Sentry.captureException(error);
          return null;
        }
      },
      [nfts, account.ethAddress, getCachedNft],
    );

    const updateSpam = useCallback(
      (token: Token) => {
        const cachedToken = getCachedNft(token.id);
        if (!cachedToken) {
          return;
        }
        const isSpam =
          cachedToken.isSpam !== undefined && cachedToken.isSpam[account.ethAddress] !== undefined
            ? !cachedToken.isSpam[account.ethAddress]
            : true;
        cachedToken.isSpam = {
          ...cachedToken.isSpam,
          [account.ethAddress]: isSpam,
        };
        save(token.id, JSON.stringify(cachedToken));

        const updatedNfts = nfts.map((nft) => {
          if (nft.id === token.id) {
            const updatedToken = { ...nft, isSpam: cachedToken.isSpam };
            return updatedToken;
          }
          return nft;
        });
        setNfts(updatedNfts);
      },
      [nfts, account.ethAddress, getCachedNft],
    );

    const setNftLastSeenAt = useCallback(
      (tokenId: string, ownerAddress: string): Token | null => {
        const nftToken = getCachedNft(tokenId);
        if (!nftToken) {
          return null;
        }
        nftToken.lastSeenAt = {
          ...nftToken.lastSeenAt,
          [ownerAddress]: new Date().toISOString(),
        };
        save(tokenId, JSON.stringify(nftToken));
        return nftToken;
      },
      [getCachedNft],
    );

    const fetchNFTMetadata = useCallback(
      async (tokenURI: string, ipfsGateways: string[] | undefined) => {
        const isIpfs = tokenURI ? tokenURI.includes('ipfs://') : false;
        let metadata = await fetchErc721Metadata(tokenURI, ipfsGateways);
        if (!metadata && !isIpfs) {
          try {
            const response = await NFTService.getMetadata(tokenURI);
            metadata = response.data;
          } catch (error) {
            Sentry.captureException(error);
            metadata = {
              name: '',
              description: '',
              image: '',
              animation_url: '',
            };
          }
        }
        return metadata;
      },
      [],
    );

    const fetchAndCacheNFT = async (token: Nft, ipfsGateways: string[] | undefined) => {
      const key = token?.id;
      try {
        const cachedToken: Token | undefined = getCachedNft(key);
        if (cachedToken) {
          return cachedToken;
        }
        const metadata = await fetchNFTMetadata(token.tokenURI, ipfsGateways);
        const updatedCachedToken = setCachedNft(token, metadata);
        return updatedCachedToken;
      } catch (error) {
        Sentry.captureException(error);
        return null;
      }
    };

    useEffect(() => {
      (async () => {
        const ownerNfts: Array<Nft> = ownerData?.owner?.tokens || [];
        const updatedNfts: Array<Nft> = [];

        ownerNfts.forEach((token) => {
          const key = token?.id;
          let updatedToken: Nft = token;
          try {
            const cachedToken: Token | undefined = getCachedNft(key);
            if (cachedToken) {
              updatedToken = {
                ...token,
                tokenName: cachedToken.metadata.name,
                isFavorite: cachedToken.isFavorite,
                isSpam: cachedToken.isSpam,
                lastSeenAt: cachedToken.lastSeenAt,
              };
            }
          } catch (error) {
            Sentry.captureException(error);
          }
          updatedNfts.push(updatedToken);
        });

        setNfts(updatedNfts);
      })();
    }, [ownerData, getCachedNft]);

    const onSelectFilterOption = useCallback(
      (option: FilterNFTsOption) => {
        setFilterOption(option);
      },
      [setFilterOption],
    );
    const onSelectSortNftsOption = useCallback(
      (option: SortNFTsOption) => {
        setSortOption(option);
      },
      [setSortOption],
    );

    const onSearchNfts = useCallback(
      (searchText: string) => {
        setSearchText(searchText);
      },
      [setSearchText],
    );

    const sortedAndFilteredNfts = useMemo(() => {
      let updatedTokens = [...nfts];

      if (filterOption === FilterNFTsOption.FAVORITE) {
        updatedTokens = updatedTokens.filter((token) =>
          token.isFavorite !== undefined && token.isFavorite[account.ethAddress] !== undefined
            ? token.isFavorite[account.ethAddress]
            : false,
        );
      }

      if (filterOption === FilterNFTsOption.SPAM) {
        updatedTokens = updatedTokens.filter((token) =>
          token.isSpam !== undefined && token.isSpam[account.ethAddress] !== undefined
            ? token.isSpam[account.ethAddress]
            : false,
        );
      }

      if (searchText) {
        updatedTokens = updatedTokens.filter(
          (token) =>
            token.tokenID.toLowerCase() === searchText.toLowerCase() ||
            token.contract.id.toLowerCase() === searchText.toLowerCase(),
        );
      }

      if (sortOption === SortNFTsOption.LAST_SEEN) {
        updatedTokens = updatedTokens.sort((a, b) => {
          if (
            a.lastSeenAt &&
            b.lastSeenAt &&
            a.lastSeenAt[account.ethAddress] &&
            b.lastSeenAt[account.ethAddress]
          ) {
            return (
              new Date(b.lastSeenAt[account.ethAddress]).getTime() -
              new Date(a.lastSeenAt[account.ethAddress]).getTime()
            );
          } else if (a.lastSeenAt && a.lastSeenAt[account.ethAddress]) {
            return -1;
          } else if (b.lastSeenAt && b.lastSeenAt[account.ethAddress]) {
            return 1;
          } else {
            return 0;
          }
        });
      } else if (sortOption === SortNFTsOption.NAME) {
        updatedTokens = updatedTokens.sort((a, b) => {
          if (!a.tokenName || a.tokenName === 'Unknown') return 1;
          if (!b.tokenName || b.tokenName === 'Unknown') return -1;
          return a.tokenName.localeCompare(b.tokenName);
        });
      }

      return updatedTokens;
    }, [nfts, searchText, filterOption, sortOption, account.ethAddress]);

    return (
      <NftDataContext.Provider
        value={{
          nfts,
          refetchNfts: refetch,
          updateFavourite,
          updateSpam,
          fetchAndCacheNFT,
          setNftLastSeenAt,
          loading,
          sortedAndFilteredNfts,
          onSearchNfts,
          onSelectFilterOption,
          onSelectSortNftsOption,
          filterOption,
          sortOption,
        }}
      >
        <ChildComponent {...props} />
      </NftDataContext.Provider>
    );
  };

  WithNftDataHOC.displayName = 'WithNftDataHOC';
  hoistNonReactStatics(WithNftDataHOC, ChildComponent);

  return WithNftDataHOC;
};

export const useNftData = () => {
  return useContext(NftDataContext);
};
